import React, { Component } from 'react';
import { getComplianceReport } from '../../../services/timeTrackingService';

/**
 * ComplianceReport - Display self vs manager check-in rates
 *
 * Props:
 * - locationId: number - installer location ID
 * - dateRange: { startDate, endDate }
 */
class ComplianceReport extends Component {
  state = {
    summary: {
      total_sessions: 0,
      self_checkin_rate: 0,
      clean_rate: 0,
      total_hours: '0.00'
    },
    byTechnician: [],
    loading: true,
    error: null
  };

  componentDidMount() {
    this.loadData();
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.dateRange.startDate !== this.props.dateRange.startDate ||
      prevProps.dateRange.endDate !== this.props.dateRange.endDate
    ) {
      this.loadData();
    }
  }

  loadData = async () => {
    const { locationId, dateRange } = this.props;

    if (!locationId) return;

    try {
      this.setState({ loading: true, error: null });

      const result = await getComplianceReport(locationId, {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      });

      this.setState({
        summary: result.summary || {
          total_sessions: 0,
          self_checkin_rate: 0,
          clean_rate: 0,
          total_hours: '0.00'
        },
        byTechnician: result.by_technician || [],
        loading: false
      });
    } catch (error) {
      console.error('Error loading compliance report:', error);
      this.setState({
        error: 'Failed to load compliance data',
        loading: false
      });
    }
  };

  // Format percentage with 1 decimal
  formatPercent = (value) => {
    if (value === null || value === undefined) return '0.0%';
    return `${parseFloat(value).toFixed(1)}%`;
  };

  // Format hours with 2 decimals
  formatHours = (hours) => {
    if (hours === null || hours === undefined) return '0.00';
    const num = typeof hours === 'string' ? parseFloat(hours) : hours;
    return num.toFixed(2);
  };

  // Get color class based on percentage threshold
  getRateColorClass = (rate) => {
    if (rate >= 90) return 'text-success';
    if (rate >= 70) return 'text-warning';
    return 'text-danger';
  };

  // Get badge variant based on percentage threshold
  getRateBadgeVariant = (rate) => {
    if (rate >= 90) return 'success';
    if (rate >= 70) return 'warning';
    return 'danger';
  };

  // Render summary cards
  renderSummaryCards = () => {
    const { summary } = this.state;

    const selfRateClass = summary.self_checkin_rate >= 90 ? 'success' : summary.self_checkin_rate >= 70 ? 'warning' : 'danger';
    const cleanRateClass = summary.clean_rate >= 90 ? 'success' : summary.clean_rate >= 70 ? 'warning' : 'danger';

    return (
      <div className="overview-cards">
        <div className="overview-card">
          <div className="number">{summary.total_sessions || 0}</div>
          <div className="label">Total Sessions</div>
        </div>
        <div className={`overview-card ${selfRateClass}`}>
          <div className="number">{this.formatPercent(summary.self_checkin_rate)}</div>
          <div className="label">Self Check-in Rate</div>
        </div>
        <div className={`overview-card ${cleanRateClass}`}>
          <div className="number">{this.formatPercent(summary.clean_rate)}</div>
          <div className="label">Clean Session Rate</div>
        </div>
        <div className="overview-card">
          <div className="number">{this.formatHours(summary.total_hours)}</div>
          <div className="label">Total Hours</div>
        </div>
      </div>
    );
  };

  render() {
    const { byTechnician, loading, error } = this.state;

    // Loading state
    if (loading) {
      return (
        <div className="text-center py-4">
          <div className="spinner-border text-secondary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p className="text-muted mt-2 mb-0">Loading compliance data...</p>
        </div>
      );
    }

    // Error state
    if (error) {
      return (
        <div className="text-danger p-3">
          <i className="fa-sharp fa-regular fa-exclamation-circle mr-2"></i>
          {error}
        </div>
      );
    }

    // Empty state
    if (!byTechnician || byTechnician.length === 0) {
      return (
        <div className="text-center py-4" style={{ background: 'transparent' }}>
          <p className="text-muted mb-0">No compliance data for the selected period.</p>
        </div>
      );
    }

    return (
      <div>
        {/* Summary Cards */}
        {this.renderSummaryCards()}

        {/* Legend */}
        <div className="mb-3 text-muted small">
          <span className="mr-3">
            <span className="badge badge-success mr-1">&nbsp;</span> 90%+
          </span>
          <span className="mr-3">
            <span className="badge badge-warning mr-1">&nbsp;</span> 70-90%
          </span>
          <span>
            <span className="badge badge-danger mr-1">&nbsp;</span> &lt;70%
          </span>
        </div>

        {/* Technician Table */}
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead className="bg-white">
              <tr>
                <th>Technician</th>
                <th className="text-right">Sessions</th>
                <th className="text-right">Self Check-ins</th>
                <th className="text-right">Manager Check-ins</th>
                <th className="text-center">Self Rate</th>
                <th className="text-center">Clean Rate</th>
                <th className="text-right">Flagged</th>
                <th className="text-right">Auto-Closed</th>
              </tr>
            </thead>
            <tbody>
              {byTechnician.map((tech) => (
                <tr key={tech.technician_id}>
                  <td>
                    <strong>{tech.technician_name}</strong>
                  </td>
                  <td className="text-right">{tech.total_sessions || 0}</td>
                  <td className="text-right">{tech.self_checkins || 0}</td>
                  <td className="text-right">
                    {(tech.manager_checkins || 0) > 0 ? (
                      <span className="text-info">{tech.manager_checkins}</span>
                    ) : (
                      '0'
                    )}
                  </td>
                  <td className="text-center">
                    <span className={`badge badge-${this.getRateBadgeVariant(tech.self_checkin_rate)}`}>
                      {this.formatPercent(tech.self_checkin_rate)}
                    </span>
                  </td>
                  <td className="text-center">
                    <span className={`badge badge-${this.getRateBadgeVariant(tech.clean_rate)}`}>
                      {this.formatPercent(tech.clean_rate)}
                    </span>
                  </td>
                  <td className="text-right">
                    {(tech.flagged_count || 0) > 0 ? (
                      <span className="text-warning">{tech.flagged_count}</span>
                    ) : (
                      '0'
                    )}
                  </td>
                  <td className="text-right">
                    {(tech.auto_closed_count || 0) > 0 ? (
                      <span className="text-danger">{tech.auto_closed_count}</span>
                    ) : (
                      '0'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }
}

export default ComplianceReport;
